/**
 * assets/free-shipping-bar.js
 * -------------------------------------------------------------------------
 * Keeps every [data-free-shipping-bar] element in sync with the live cart
 * total, with no page reload required.
 *
 * How it stays in sync:
 *   1. On load, it fetches /cart.js once and renders the current state.
 *   2. It listens for the cart-change custom events most Shopify themes
 *      already dispatch (cart:updated, cart:refresh, cart:build,
 *      cart:change, cart:added, ajaxCart:updated).
 *   3. As a safety net that works with ANY theme, it wraps window.fetch
 *      and window.XMLHttpRequest so that any successful call to
 *      /cart/add.js, /cart/change.js, /cart/update.js or /cart/clear.js
 *      automatically triggers a refresh — even if the theme fires no
 *      custom event at all.
 *
 * Manual refresh (e.g. after a custom AJAX call):
 *   window.FreeShippingBar.refresh();
 *
 * Manual refresh with a cart object you already have (avoids an extra
 * request, e.g. inside a `cart/add.js` .then()):
 *   window.FreeShippingBar.refresh(cartObject);
 */
(function () {
  'use strict';

  var SELECTOR = '[data-free-shipping-bar]';
  var CART_MUTATING_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  function isCartMutatingUrl(url) {
    if (!url) return false;
    var path = String(url).split('?')[0];
    return CART_MUTATING_PATHS.some(function (p) {
      return path.indexOf(p) !== -1;
    });
  }

  function formatMoney(cents, currencyCode) {
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode || 'USD',
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (e) {
      // Fallback if currencyCode is missing/invalid
      return '$' + amount.toFixed(2);
    }
  }

  function renderBar(el, cart) {
    var thresholdCents = parseInt(el.getAttribute('data-threshold-cents'), 10) || 0;
    var remainingText = el.getAttribute('data-remaining-text') || 'to free shipping';
    var achievedText = el.getAttribute('data-achieved-text') || "You've unlocked free shipping!";
    var totalCents = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    var currency = (cart && cart.currency) || window.Shopify && window.Shopify.currency && window.Shopify.currency.active;

    var remainingCents = thresholdCents - totalCents;
    var achieved = remainingCents <= 0;

    var textEl = el.querySelector('[data-free-shipping-bar-text]');
    if (textEl) {
      textEl.textContent = achieved
        ? achievedText
        : formatMoney(remainingCents, currency) + ' ' + remainingText;
      textEl.setAttribute('data-achieved', achieved ? 'true' : 'false');
    }

    var fillEl = el.querySelector('[data-free-shipping-bar-fill]');
    if (fillEl) {
      var pct = thresholdCents > 0 ? Math.min(100, Math.max(0, (totalCents / thresholdCents) * 100)) : 100;
      fillEl.style.width = pct + '%';
    }

    el.classList.toggle('free-shipping-bar--achieved', achieved);
    el.dispatchEvent(new CustomEvent('free-shipping-bar:rendered', { detail: { cart: cart, achieved: achieved } }));
  }

  function renderAll(cart) {
    var bars = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      renderBar(bars[i], cart);
    }
  }

  function fetchCartAndRender() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        renderAll(cart);
        return cart;
      })
      .catch(function () {
        /* fail silently: leave last-known state on screen */
      });
  }

  function refresh(cartMaybe) {
    if (cartMaybe && typeof cartMaybe.total_price === 'number') {
      renderAll(cartMaybe);
      return Promise.resolve(cartMaybe);
    }
    return fetchCartAndRender();
  }

  // ---- 1. Initial render on load -------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refresh();
    });
  } else {
    refresh();
  }

  // ---- 2. Listen for common theme cart events -------------------------
  [
    'cart:updated',
    'cart:refresh',
    'cart:build',
    'cart:change',
    'cart:added',
    'ajaxCart:updated'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, function (evt) {
      var detail = evt && evt.detail;
      if (detail && typeof detail.total_price === 'number') {
        refresh(detail);
      } else if (detail && detail.cart && typeof detail.cart.total_price === 'number') {
        refresh(detail.cart);
      } else {
        refresh();
      }
    });
  });

  // ---- 3. Universal fallback: watch fetch() for cart mutations --------
  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0] && arguments[0].url ? arguments[0].url : arguments[0];
      var shouldRefresh = isCartMutatingUrl(url);
      return originalFetch.apply(this, arguments).then(function (response) {
        if (shouldRefresh && response && response.ok) {
          // Let the theme's own .then() run first, then sync our bar.
          setTimeout(fetchCartAndRender, 0);
        }
        return response;
      });
    };
  }

  // ---- 3b. Universal fallback: watch XMLHttpRequest for cart mutations
  if (window.XMLHttpRequest) {
    var originalOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__fsbShouldRefresh = isCartMutatingUrl(url);
      return originalOpen.apply(this, arguments);
    };

    var originalSend = window.XMLHttpRequest.prototype.send;
    window.XMLHttpRequest.prototype.send = function () {
      if (this.__fsbShouldRefresh) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            setTimeout(fetchCartAndRender, 0);
          }
        });
      }
      return originalSend.apply(this, arguments);
    };
  }

  // ---- Public API -------------------------------------------------------
  window.FreeShippingBar = {
    refresh: refresh
  };
})();
